import React, {Component, ReactElement} from 'react'
import NatTokenRepo from '../token/repository/NatTokenRepo'
import {NatGetToken} from '../token/repository/NatGetToken'
import NatPasswordRepo from '../password/repository/NatPasswordRepo'
import NatResidentUserRepo from '../residentUser/repository/NatResidentUserRepo'
import NatStepComponent, {DIspStepInfo, Step} from '../common/resident/NatStepComponent'
import {NatGetResidentUserToken} from '../residentUser/repository/NatGetResidentUserToken'
import {
    ContentsSubTitleInner,
    ContentsSubTitleWrapper,
    Note,
    WideMainContainer,
} from '../../../styledComponents/ContentsArea'
import {
    DisplayBlockDiv,
    DisplayCheckItemSelectionLabel,
    DisplayCheckItemSelectionName,
    DisplayItemInput,
    DisplayItemInputLabelRequired,
    DisplayItemInputTitleRequired,
    DisplayItemTable,
    DisplayItemTableInput,
    DisplayItemTableLabel,
    FieldErrorLabel,
} from '../../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {Messages} from '../../../Messages'
import {CenterPlainLinkStyledButton, PrimaryStyledButton} from '../../../styledComponents/Button'
import {Box} from '@mui/material'
import {Nullable} from '../../../app/TypeHelpers'
import {ErrorResponseBody} from '../../../network/response/ErrorResponseBody'
import {BaseHttpError} from '../../../network/errors/baseHandling/BaseHttpError'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {VALID_OR_NOT} from '../../../CodeMaster'
import {getQueryParameter, NatAsyncCallFunction} from '../../common/util/Natutil'
import {createResidentPage, ResidentPagePropsSet} from '../../../components/shared/hoc/createResidentPage'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {Location} from 'react-router-dom'

interface FieldsError {
    password: Error[]
    passwordConfirm: Error[]
}

interface Error {
    message: string
    hasError: boolean
}

type Field = 'password' | 'passwordConfirm'

interface Props {
    residentUserRepo: NatResidentUserRepo
    tokenRepo: NatTokenRepo,
    passwordRepo: NatPasswordRepo,
    location: Location,
}

interface States {
    password: string
    passwordConfirm: string
    dispPassword: string,
    passwordResetButtonDispFlg: boolean
    familyName: string
    firstName: string
    mailAddressPc: string
    mailAddressMb: string
    dIspStepInfo: DIspStepInfo
    fieldsError: Partial<FieldsError>,
    isLoadCompleted: boolean
}

const PASSWORD_LABEL = '新しいパスワード'
const PASSWORD_CONFIRM_LABEL = '新しいパスワード（確認用）'

class NatResidentPasswordResetInputPage extends Component<Props & ResidentPagePropsSet, States> {
    queryToken: string

    constructor(props: Props & ResidentPagePropsSet) {
        super(props)
        this.state = {
            password: '',
            passwordConfirm: '',
            dispPassword: VALID_OR_NOT.NOT.value,
            passwordResetButtonDispFlg: true,
            familyName: '',
            firstName: '',
            mailAddressPc: '',
            mailAddressMb: '',
            dIspStepInfo: {reSetting: true, complete: true},
            fieldsError: {},
            isLoadCompleted: false,
        }

        this.queryToken = getQueryParameter('token', this.props.location.search)

        this.backLoginButtonClick = this.backLoginButtonClick.bind(this)
        this.passwordResetButtonClick = this.passwordResetButtonClick.bind(this)
        this.handlePassworDispTypeChange = this.handlePassworDispTypeChange.bind(this)
        this.onChangePassword = this.onChangePassword.bind(this)
    }

    async componentDidMount(): Promise<void> {

        // トークンチェック
        if (this.queryToken === '') {
            this.props.navigate('/resident/error', {state: {message: Messages.ERESR0007.toString()}})
            return
        }

        await this.props.eventEmitter.showLoading()

        try {
            // リクエストパラメータのトークンをチェック
            const tokenCheckResult: Nullable<NatGetToken> = await NatAsyncCallFunction(
                this.props.tokenRepo.getToken.bind(
                    this.props.tokenRepo, this.queryToken), this.props.navigate)

            if (tokenCheckResult === null) {
                return
            }

            if (tokenCheckResult && tokenCheckResult.details === null) {
                this.props.navigate('/resident/error', {state: {message: Messages.ERESR0007.toString()}})
                return
            }

            // ユーザ情報を取得
            const residentUserTokenResult: Nullable<NatGetResidentUserToken> = await NatAsyncCallFunction(
                this.props.residentUserRepo.getResidentUserToken.bind(
                    this.props.residentUserRepo, this.queryToken), this.props.navigate)

            if (residentUserTokenResult && residentUserTokenResult.details) {
                this.setState({
                    familyName: residentUserTokenResult.details.familyName,
                    firstName: residentUserTokenResult.details.firstName,
                    mailAddressPc: residentUserTokenResult.details.mailAddressPc,
                    mailAddressMb: residentUserTokenResult.details.mailAddressMb,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }


    onChangePassword(event: React.ChangeEvent<HTMLInputElement>): void {
        let password: string = this.state.password
        let passwordConfirm: string = this.state.passwordConfirm

        switch (event.target.name) {
            case 'password':
                password = event.target.value
                break
            case 'passwordConfirm':
                passwordConfirm = event.target.value
                break
            default:
                break
        }

        if (password === '' || passwordConfirm === '') {
            this.setState({passwordResetButtonDispFlg: true})
        } else {
            this.setState({passwordResetButtonDispFlg: false})
        }

        this.setState({password: password, passwordConfirm: passwordConfirm})
    }

    // パスワードを表示する
    handlePassworDispTypeChange(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.checked) {
            this.setState({dispPassword: VALID_OR_NOT.VALID.value})
        } else {
            this.setState({dispPassword: VALID_OR_NOT.NOT.value})
        }
    }

    // 新しいパスワードを設定する
    async passwordResetButtonClick(): Promise<void> {
        await this.clearMessage()

        // 相関チェック
        if (this.state.password !== this.state.passwordConfirm) {
            this.setFieldErrorMessage(
                'passwordConfirm',
                Messages.EOPCC0019.toString(PASSWORD_LABEL, PASSWORD_CONFIRM_LABEL),
            )
            return
        }

        await this.props.eventEmitter.showLoading()
        try {
            const ret: Nullable<void> = await NatAsyncCallFunction(
                this.props.passwordRepo.putPasswordToken.bind(
                    this.props.passwordRepo, this.queryToken, this.state.password), this.props.navigate)

            if (ret) {
                this.props.navigate(
                    `/resident/passwordResetComplete`,
                )
            }
        } catch (HttpError) {
            const errorResponseBody: Nullable<ErrorResponseBody> = (HttpError as BaseHttpError).responseBody
            if (errorResponseBody !== null) {
                errorResponseBody.requestItemErrors.forEach((item: RequestItemError) => {
                    if (item.fields) {
                        item.fields.forEach((field) => {
                            this.setFieldErrorMessage(field, item.message)
                        })
                    }
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    };

    // ログインページへ
    backLoginButtonClick(): void {
        this.props.navigate(
            `/resident/login`,
        )
    };

    // メッセージ設定（フィールド）
    setFieldErrorMessage(field: string, message: string): void {
        if (field === 'password' || field === 'passwordConfirm') {
            let error: Error[] | undefined = this.state.fieldsError[field]
            if (error === undefined) {
                error = [{message: message, hasError: true}]
            } else {
                error.push({message: message, hasError: true})
            }
            this.setState(({fieldsError}) => (
                {
                    fieldsError: {
                        ...fieldsError,
                        [field]: error,
                    },
                }))
        }
    }

    // メッセージ取得（フィールド）
    getFieldErrorMessage(fieldId: Field): Nullable<ReactElement> {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        if (field !== undefined) {
            return (
                <>
                    {field.map((error, index) => (
                        <FieldErrorLabel key={index}>
                            {error.message}
                        </FieldErrorLabel>
                    ))}
                </>
            )
        } else {
            return null
        }
    }

    // フィールドエラー状態取得
    getFieldErrorStatus(fieldId: Field): boolean {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        return !!(field !== undefined && field[0] !== undefined && field[0].hasError)
    }

    // メッセージクリア
    clearMessage(): void {
        this.setState({fieldsError: {}})
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <NatStepComponent
                    dIspStepInfo={this.state.dIspStepInfo}
                    nowStep={Step.RESETTING}
                />
                <ContentsSubTitleWrapper>
                    <ContentsSubTitleInner>
                        パスワードの再設定
                    </ContentsSubTitleInner>
                </ContentsSubTitleWrapper>
                <WideMainContainer>
                    <BlockSpacer height={'15px'}/>
                    <Box mb={'30px'}>
                        <DisplayTable>
                            <DisplayItemTableLabel>
                                氏名
                            </DisplayItemTableLabel>
                            <DisplayItemTableInput>
                                {this.state.familyName} {this.state.firstName}
                            </DisplayItemTableInput>
                        </DisplayTable>
                        <DisplayTable>
                            <DisplayItemTableLabel>
                                メールアドレス
                            </DisplayItemTableLabel>
                            <DisplayItemTableInput>
                                {this.state.mailAddressPc}
                            </DisplayItemTableInput>
                        </DisplayTable>
                    </Box>
                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>{PASSWORD_LABEL}</DisplayItemInputTitleRequired>
                            <DisplayItemInput
                                type={(this.state.dispPassword === VALID_OR_NOT.NOT.value) ? 'password' : 'text'}
                                autoComplete={'new-password'}
                                name="password"
                                defaultValue={this.state.password}
                                maxLength={16}
                                onChange={this.onChangePassword}
                                hasError={this.getFieldErrorStatus('password')}
                                autoFocus={true}/>
                            <Note>{Messages.IRESR0005.toString('')}</Note>
                        </DisplayItemInputLabelRequired>
                    </DisplayBlockDiv>
                    {this.getFieldErrorMessage('password')}
                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>{PASSWORD_CONFIRM_LABEL}</DisplayItemInputTitleRequired>
                            <DisplayItemInput
                                type={(this.state.dispPassword === VALID_OR_NOT.NOT.value) ? 'password' : 'text'}
                                autoComplete={'new-password'}
                                name="passwordConfirm"
                                defaultValue={this.state.passwordConfirm}
                                maxLength={16}
                                onChange={this.onChangePassword}
                                hasError={this.getFieldErrorStatus('passwordConfirm')}/>
                            <DisplayCheckItemSelectionLabel>
                                <DisplayCheckItemSelectionName>
                                    <input type="checkbox"
                                           onChange={this.handlePassworDispTypeChange}/>
                                    パスワードを表示する
                                </DisplayCheckItemSelectionName>
                            </DisplayCheckItemSelectionLabel>
                        </DisplayItemInputLabelRequired>
                    </DisplayBlockDiv>
                    {this.getFieldErrorMessage('passwordConfirm')}
                    <BlockSpacer height={'5px'}/>
                </WideMainContainer>
                <Box height={'30px'}/>
                <PrimaryStyledButton
                    onClick={this.passwordResetButtonClick}
                    disabled={this.state.passwordResetButtonDispFlg}>
                    新しいパスワードを設定する
                </PrimaryStyledButton>
                <Box height={'30px'}/>
                <CenterPlainLinkStyledButton onClick={this.backLoginButtonClick}>
                    ログイン画面へ戻る
                </CenterPlainLinkStyledButton>
                <Box height={'30px'}/>
            </>
        )
    }
}

export default createResidentPage(NatResidentPasswordResetInputPage)

// テーブル
export const DisplayTable = styled(DisplayItemTable)`
  padding: 0;

`
